import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getChatMessagesListener, getChatDocumentListener, markMessagesAsRead } from '../../services/chatService';
import { useAuth } from '../../Context/AuthContext';
import { useTheme } from '../../Context/ThemeContext'; // Add theme context
import MessageInput from './MessageInput';
import Loader from '../Loader';
import { format } from 'date-fns';
import { FaCheck, FaCheckDouble, FaFileAlt } from 'react-icons/fa';

import { FaLock } from 'react-icons/fa';

const useIntersectionObserver = (options) => {
  const [entry, setEntry] = useState(null);
  const [node, setNode] = useState(null);

  const observer = useRef(
    new IntersectionObserver(([entry]) => setEntry(entry), options)
  );

  useEffect(() => {
    const { current: currentObserver } = observer;
    currentObserver.disconnect();

    if (node) currentObserver.observe(node);

    return () => currentObserver.disconnect();
  }, [node, options]);

  return [setNode, entry];
};

const ChatView = ({ chatId, receiverUserId, receiverName, receiverPic }) => {
  const { currentUser } = useAuth();
  const { isDarkMode } = useTheme(); // Get theme state
  const [messages, setMessages] = useState([]);
  const [isLoadingMessages, setIsLoadingMessages] = useState(true);
  const [chatData, setChatData] = useState(null);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const messageListRef = useRef(null);
  const [mySecretKey, setMySecretKey] = useState(null);
  const [theirPublicKey, setTheirPublicKey] = useState(null);
  const [keysLoaded, setKeysLoaded] = useState(false);
  
  // useEffect(() => {
  //   const loadKeys = async () => {
  //     if (currentUser?.uid && receiverUserId) {
  //       try {
  //         const sk = await getMyStoredSecretKey(currentUser.uid);
  //         const pk = await getUserPublicKey(receiverUserId);
  //         if (sk && pk) {
  //           setMySecretKey(sk);
  //           setTheirPublicKey(pk);
  //           setKeysLoaded(true);
  //         } else {
  //           setError("Could not load encryption keys. Messages may not decrypt.");
  //           if (!sk) console.error("Current user's secret key is missing.");
  //           if (!pk) console.error(`Receiver's (${receiverUserId}) public key is missing.`);
  //         }
  //       } catch (e) {
  //         console.error("Error loading keys:", e);
  //         setError("Error initializing secure chat session.");
  //       }
  //     }
  //   };
  //   loadKeys();
  // }, [currentUser?.uid, receiverUserId]);

  const formatTimestamp = (timestamp) => {
    if (!timestamp || !timestamp.toDate) return 'sending...';
    try {
      return format(timestamp.toDate(), 'Pp');
    } catch (e) {
      return 'invalid date';
    }
  };

  const unreadMessagesRef = useRef(new Set());
  const handleIntersection = useCallback((entry) => {
    if (!entry || !currentUser) return;
    const messageId = entry.target.getAttribute('data-message-id');
    const senderId = entry.target.getAttribute('data-sender-id');
    if (!messageId || !senderId) return;
    if (entry.isIntersecting && senderId === receiverUserId) {
      if (unreadMessagesRef.current.has(messageId)) {
        markMessagesAsRead(chatId, [messageId]);
        unreadMessagesRef.current.delete(messageId);
      }
    }
  }, [chatId, receiverUserId, currentUser]);

  useEffect(() => {
    unreadMessagesRef.current.clear();
    const observer = new IntersectionObserver((entries) => entries.forEach(handleIntersection), {});
    const messageElements = messageListRef.current?.querySelectorAll('.message-item[data-read="false"][data-sender-id="' + receiverUserId + '"]');
    messageElements?.forEach(el => {
      const messageId = el.getAttribute('data-message-id');
      if (messageId) {
        unreadMessagesRef.current.add(messageId);
        observer.observe(el);
      }
    });
    return () => observer.disconnect();
  }, [messages, receiverUserId, handleIntersection]);

  useEffect(() => {
    // if (!chatId || !currentUser?.uid || !keysLoaded) {
    //   setIsLoadingMessages(false);
    //   if (!keysLoaded && currentUser?.uid && receiverUserId) {
    //     setError("Initializing secure session...");
    //   } else if (chatId && currentUser?.uid) {
    //     setError("Chat information is missing or keys not loaded.");
    //   }
    //   return;
    // }

    setIsLoadingMessages(true);
    setError(null);

    const handleNewMessages = async (fetchedMessages) => {
      setIsLoadingMessages(true);
      // const decryptedMessages = await Promise.all(
      //   fetchedMessages.map(async (msg) => {
      //     if (msg.type === 'text' && msg.ciphertext && msg.nonce && msg.encryptionType?.startsWith("e2ee/")) {
      //       try {
      //         let senderPublicKey;
      //         if (msg.senderId === currentUser.uid) {
      //           senderPublicKey = theirPublicKey;
      //         } else {
      //           senderPublicKey = await getUserPublicKey(msg.senderId);
      //         }

      //         if (!mySecretKey || !senderPublicKey) {
      //           throw new Error("Required key for decryption is missing.");
      //         }

      //         const decryptedText = decryptMessage(
      //           msg.ciphertext,
      //           msg.nonce,
      //           senderPublicKey,
      //           mySecretKey
      //         );
      //         return { ...msg, text: decryptedText, isEncrypted: true };
      //       } catch (decryptionError) {
      //         console.error("Failed to decrypt message:", msg.id, decryptionError);
      //         return { ...msg, text: "[Message decryption failed 🔒]", error: true, isEncrypted: true };
      //       }
      //     }
      //     return msg;
      //   })
      // );
          console.log("Listener fetched messages:", fetchedMessages); // ADD THIS

      setMessages(fetchedMessages);
      setIsLoadingMessages(false);
      setError(null);
    };

    let unsubscribeMessages;
    try {
      unsubscribeMessages = getChatMessagesListener(chatId, handleNewMessages);
    } catch (listenerError) {
      console.error("Failed to set up listener:", listenerError);
      setError("Could not load messages.");
      setIsLoadingMessages(false);
    }

    return () => {
      if (unsubscribeMessages && typeof unsubscribeMessages === 'function') {
        unsubscribeMessages();
      }
    };
  }, [chatId, currentUser?.uid, keysLoaded, mySecretKey, theirPublicKey]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const isReceiverTyping = chatData?.typingStatus?.[receiverUserId] === true;
  const isLoading = isLoadingMessages;

  if (isLoading) {
    return <div className="flex justify-center items-center h-full"><Loader /></div>;
  }

  if (error) {
    return <div className="flex justify-center items-center h-full text-red-600 p-4">{error}</div>;
  }

  return (
    <div className={`flex flex-col h-full ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'}`}>
      <div ref={messageListRef} className={`flex-grow overflow-y-auto p-4 space-y-4 ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
        {messages.map((msg) => {
          const isSender = msg.senderId === currentUser.uid;
          const isRead = msg.read === true;

          return (
            <div
              key={msg.id}
              className={`message-item flex ${isSender ? 'justify-end' : 'justify-start'}`}
              data-message-id={msg.id}
              data-sender-id={msg.senderId}
              data-read={msg.read}
            >
              <div className={`flex items-end max-w-xs md:max-w-md ${isSender ? 'flex-row-reverse' : 'flex-row'}`}>
                {!isSender && (
                  <img
                    src={receiverPic || '/default-profile.jpg'}
                    alt={receiverName || 'User'}
                    className="w-6 h-6 rounded-full object-cover mr-2 self-start"
                  />
                )}
                <div
                  className={`px-4 py-2 rounded-lg shadow-md ${
                    isSender
                      ? isDarkMode
                        ? 'bg-indigo-600 text-white rounded-br-none'
                        : 'bg-indigo-500 text-white rounded-br-none'
                      : isDarkMode
                        ? 'bg-gray-700 text-white rounded-bl-none'
                        : 'bg-gray-200 text-gray-800 rounded-bl-none'
                  }`}
                >
                {/* Updated shared startup rendering */}
          {msg.type === "startup_share" ? (
            <div className={`p-3 rounded-lg border ${
              isSender
                ? isDarkMode
                  ? 'bg-indigo-700 border-indigo-500'
                  : 'bg-indigo-100 border-indigo-300'
                : isDarkMode
                  ? 'bg-gray-700 border-gray-600'
                  : 'bg-gray-100 border-gray-300'
            }`}>
              <p className={`${
                isSender
                  ? isDarkMode
                    ? 'text-indigo-100'
                    : 'text-indigo-800'
                  : isDarkMode
                    ? 'text-gray-200'
                    : 'text-gray-700'
              }`}>
                {msg.content.split('\n\n')[0]}
              </p>
              <a 
                href={`/startup/${msg.startupId}`}
                className={`mt-2 inline-block ${
                  isSender
                    ? isDarkMode
                      ? 'text-indigo-300 hover:text-indigo-200'
                      : 'text-indigo-600 hover:text-indigo-700'
                    : isDarkMode
                      ? 'text-blue-400 hover:text-blue-300'
                      : 'text-blue-600 hover:text-blue-700'
                } hover:underline`}
              >
                View Startup Profile
              </a>
            </div>
          ) : msg.type === 'image' && msg.fileUrl ? (
                    <a href={msg.fileUrl} target="_blank" rel="noopener noreferrer" title={msg.fileName || 'View image'}>
                      <img
                        src={msg.fileUrl}
                        alt={msg.fileName || 'Chat image'}
                        className="max-w-full h-auto rounded my-1 max-h-60 object-contain cursor-pointer"
                        loading="lazy"
                      />
                    </a>
                  ) : msg.type === 'file' && msg.fileUrl ? (
                    <a
                      href={msg.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      download={msg.fileName || 'file'}
                      className={`flex items-center space-x-2 p-2 rounded my-1 ${
                        isSender
                          ? isDarkMode
                            ? 'hover:bg-indigo-700'
                            : 'hover:bg-indigo-600'
                          : isDarkMode
                            ? 'hover:bg-gray-600'
                            : 'hover:bg-gray-300'
                      }`}
                      title={`Download ${msg.fileName || 'file'}`}
                    >
                      <FaFileAlt className={`flex-shrink-0 ${
                        isSender
                          ? isDarkMode
                            ? 'text-indigo-200'
                            : 'text-indigo-100'
                          : isDarkMode
                            ? 'text-gray-400'
                            : 'text-gray-600'
                      }`} size={20} />
                      <span className={`text-sm truncate ${
                        isSender ? 'text-white' : isDarkMode ? 'text-gray-100' : 'text-gray-800'
                      }`}>
                        {msg.fileName || 'Download File'}
                      </span>
                    </a>
                  ) : (
                    <p className={`text-sm whitespace-pre-wrap break-words ${

                    isSender
                      ? isDarkMode
                        ? 'text-indigo-200'
                        : 'text-indigo-100'
                      : isDarkMode
                        ? 'text-gray-400'
                        : 'text-gray-500'
                  }`}>
                    {msg.text || msg.content}
            </p>
          )}
          
          <div className={`flex items-center justify-end text-xs mt-1 ${
            isSender
              ? isDarkMode
                ? 'text-indigo-200'
                : 'text-indigo-100'
              : isDarkMode
                ? 'text-gray-400'
                : 'text-gray-500'
          }`}>
                    <span className={isSender ? 'mr-1' : ''}>{formatTimestamp(msg.timestamp)}</span>
                    {isSender && (
                      <span className="ml-1">
                        {isRead ? <FaCheckDouble className={isDarkMode ? "text-blue-300" : "text-blue-500"} /> : <FaCheck className={isDarkMode ? "text-indigo-300" : "text-indigo-400"} />}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {isReceiverTyping && (
          <div className="flex justify-start">
            <div className="flex items-end max-w-xs md:max-w-md flex-row">
              <img
                src={receiverPic || '/default-profile.jpg'}
                alt={receiverName || 'User'}
                className="w-6 h-6 rounded-full object-cover mr-2 self-start"
              />
              <div className={`px-4 py-2 rounded-lg ${
                isDarkMode ? 'bg-gray-700 text-white' : 'bg-gray-200 text-gray-600'
              } rounded-bl-none animate-pulse text-sm italic`}>
                typing...
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <MessageInput
        chatId={chatId}
        senderId={currentUser?.uid}
        receiverId={receiverUserId}
      />
    </div>
  );
};

export default ChatView;